import React, { useState, useEffect, useRef } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const SALUTATION_OPTIONS = ['Mr.', 'Mrs.', 'Miss', 'Dr.', 'Ms.'] as const;
type Salutation = typeof SALUTATION_OPTIONS[number];

interface IndividualNameInputProps {
  value: { salutation?: string; first_name?: string; last_name?: string };
  onChange: (val: { salutation: string; first_name: string; last_name: string }) => void;
  disabled?: boolean;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
  /** Gate-level field errors passed down from JourneyPage after Continue is pressed */
  fieldErrors?: Record<string, string>;
}

function validateName(value: string, label: string): string {
  const trimmed = (value || '').trim();
  if (!trimmed) return `${label} is required`;
  if (!/^[a-zA-Z\s'-]+$/.test(trimmed)) return `${label} must contain letters only`;
  if (trimmed.length < 2) return `${label} must be at least 2 characters`;
  return '';
}

/**
 * Last name is OPTIONAL — a blank value is accepted, because a single-word legal
 * name (mononym) is common on Indian KYC documents and must not block the
 * journey. The format rules still apply once something has been typed, so a
 * genuine typo is still caught.
 */
function validateOptionalName(value: string, label: string): string {
  const trimmed = (value || '').trim();
  if (!trimmed) return '';
  if (!/^[a-zA-Z\s'-]+$/.test(trimmed)) return `${label} must contain letters only`;
  if (trimmed.length < 2) return `${label} must be at least 2 characters`;
  return '';
}

const IndividualNameInput: React.FC<IndividualNameInputProps> = ({
  value,
  onChange,
  disabled,
  registerSubmit,
  fieldErrors,
}) => {
  const salutation = value?.salutation || '';
  const firstName = value?.first_name || '';
  const lastName = value?.last_name || '';

  const [touched, setTouched] = useState<{
    salutation: boolean;
    first_name: boolean;
    last_name: boolean;
  }>({
    salutation: false,
    first_name: false,
    last_name: false,
  });

  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    if (!registerSubmit) return;
    registerSubmit(async () => {
      setTouched({ salutation: true, first_name: true, last_name: true });
      const v = valueRef.current;
      const salutationErr = !(v?.salutation || '').trim() ? 'Salutation is required' : '';
      const firstErr = validateName(v?.first_name || '', 'First name');
      const lastErr = validateOptionalName(v?.last_name || '', 'Last name');
      return !salutationErr && !firstErr && !lastErr;
    });
  }, [registerSubmit]);

  const handleSalutationChange = (val: string) => {
    onChange({ salutation: val, first_name: firstName, last_name: lastName });
    setTouched(prev => ({ ...prev, salutation: true }));
  };

  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ salutation, first_name: e.target.value, last_name: lastName });
  };

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ salutation, first_name: firstName, last_name: e.target.value });
  };

  const salutationError =
    fieldErrors?.['salutation'] ||
    (touched.salutation && !salutation.trim() ? 'Salutation is required' : '');
  const firstNameError =
    fieldErrors?.['first_name'] ||
    (touched.first_name ? validateName(firstName, 'First name') : '');
  const lastNameError =
    fieldErrors?.['last_name'] ||
    (touched.last_name ? validateOptionalName(lastName, 'Last name') : '');

  return (
    <div className="space-y-4">
      {/* Salutation dropdown */}
      <div className="space-y-1.5">
        <Label htmlFor="salutation" className="text-gray-500 font-medium">
          Salutation <span className="text-red-500">*</span>
        </Label>
        <Select
          value={salutation}
          onValueChange={handleSalutationChange}
          disabled={disabled}
        >
          <SelectTrigger
            id="salutation"
            className={`border-gray-200 focus:border-intellend-green focus:ring-intellend-green rounded-xl py-6 h-auto${salutationError ? ' border-red-400 focus:border-red-500' : ''}`}
          >
            <SelectValue placeholder="Select salutation" />
          </SelectTrigger>
          <SelectContent>
            {SALUTATION_OPTIONS.map(opt => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {salutationError && (
          <p className="text-xs text-red-500 mt-0.5">{salutationError}</p>
        )}
      </div>

      {/* First Name */}
      <div className="space-y-1.5">
        <Label htmlFor="first_name" className="text-gray-500 font-medium">
          First Name <span className="text-red-500">*</span>
        </Label>
        <Input
          id="first_name"
          type="text"
          value={firstName}
          onChange={handleFirstNameChange}
          onBlur={() => setTouched(prev => ({ ...prev, first_name: true }))}
          placeholder="Enter first name"
          disabled={disabled}
          className={`border-gray-200 focus:border-intellend-green focus:ring-intellend-green rounded-xl py-6 ${firstNameError ? 'border-red-400 focus:border-red-500' : ''}`}
        />
        {firstNameError && (
          <p className="text-xs text-red-500 mt-0.5">{firstNameError}</p>
        )}
      </div>

      {/* Last Name */}
      <div className="space-y-1.5">
        <Label htmlFor="last_name" className="text-gray-500 font-medium">
          Last Name
        </Label>
        <Input
          id="last_name"
          type="text"
          value={lastName}
          onChange={handleLastNameChange}
          onBlur={() => setTouched(prev => ({ ...prev, last_name: true }))}
          placeholder="Enter last name"
          disabled={disabled}
          className={`border-gray-200 focus:border-intellend-green focus:ring-intellend-green rounded-xl py-6 ${lastNameError ? 'border-red-400 focus:border-red-500' : ''}`}
        />
        {lastNameError && (
          <p className="text-xs text-red-500 mt-0.5">{lastNameError}</p>
        )}
      </div>
    </div>
  );
};

export default IndividualNameInput;
